import { useState, useEffect } from "react";
import { APP_CONFIG } from "./appConfig";
import { APP_VERSION } from "./constants/version";
import { applyTheme } from "./constants/themes";
import { checkNewBadges } from "./constants/badges";
import { SEED, } from "./constants/seed";
import {
  DEMO_MODE, fbGet, fbSet,
  loadLocal, saveLocal,
  getInitialSettings, saveSettingsLocal, mergeSettings,
} from "./utils/firebase";
import { calcPointsEarned, calcMeaningPoints, calcSynonymPoints, calcAntonymPoints } from "./utils/points";
import {
  buildMasterPool, selectWords, selectMeaningWords,
  updateProgress, updateMeaningProgress, updateSynProgress, updateAntProgress,
} from "./utils/words";
import { BgWrap } from "./components/shared/BgWrap";
import { BadgeToast } from "./components/shared/BadgeToast";
import { NotificationBell } from "./components/shared/NotificationBell";
// import { LoginScreen } from "./components/auth/LoginScreen"; // replaced by PlannerLoginScreen
// import { ForceResetPin } from "./components/auth/ForceResetPin"; // replaced by PlannerForceResetPin
import { PlannerLoginScreen } from "./components/auth/PlannerLoginScreen";
import { PlannerForceResetPin } from "./components/auth/PlannerForceResetPin";
import { SpellingTest } from "./components/quizzes/SpellingTest";
import { MeaningQuiz } from "./components/quizzes/MeaningQuiz";
import { SynonymQuiz } from "./components/quizzes/SynonymQuiz";
import { AntonymQuiz } from "./components/quizzes/AntonymQuiz";
import { ResultsScreen } from "./components/child/ResultsScreen";
import { MeaningResults } from "./components/child/MeaningResults";
import { ChildHome } from "./components/child/ChildHome";
// import { ParentDashboard } from "./components/parent/ParentDashboard"; // replaced by PlannerDashboard
import { PlannerDashboard } from "./components/parent/PlannerDashboard";
import { ProfileSettings } from "./components/shared/ProfileSettings";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Lora:wght@500;700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg:#f8fafc;--surface:#fff;--surface2:#f1f5f9;--border:#e2e8f0;
  --ink:#1e293b;--navy:#0f172a;--muted:#64748b;
  --blue:#2563eb;--blue-light:#dbeafe;
  --green:#16a34a;--green-light:#dcfce7;
  --red:#dc2626;--red-light:#fee2e2;
  --amber:#d97706;--amber-light:#fef3c7;
  --topbar-bg:#fff;--hero-grad:linear-gradient(135deg,#1e3a5f,#2563eb);
  --radius:16px;--shadow:0 4px 24px rgba(0,0,0,.08);--shadow-lg:0 8px 40px rgba(0,0,0,.14);
}
body{font-family:'Nunito',sans-serif;background:var(--bg);color:var(--ink);min-height:100vh;transition:background .3s,color .3s}
.app{min-height:100vh;display:flex;flex-direction:column}
.bg-wrap{position:fixed;inset:0;z-index:0;pointer-events:none;background-size:cover;background-position:center;transition:opacity .4s}
.bg-overlay{position:fixed;inset:0;z-index:0;pointer-events:none;background:rgba(0,0,0,.35)}
.above-bg{position:relative;z-index:1}

/* LOGIN */
.login-wrap{min-height:100vh;background:linear-gradient(135deg,#0f172a 0%,#1e3a5f 50%,#0f172a 100%);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2rem;position:relative;overflow:hidden}
.login-wrap::before{content:'';position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse at 25% 25%,rgba(37,99,235,.18) 0%,transparent 55%),radial-gradient(ellipse at 75% 75%,rgba(124,58,237,.12) 0%,transparent 55%)}
.login-logo{font-size:3rem;margin-bottom:.5rem;position:relative;z-index:1}
.login-title{font-family:'Lora',serif;font-weight:700;font-size:2.2rem;color:#fff;text-align:center;position:relative;z-index:1}
.login-sub{color:rgba(255,255,255,.45);font-size:.9rem;margin:.25rem 0 2.5rem;text-align:center;position:relative;z-index:1}
.profile-grid{display:flex;gap:1.25rem;flex-wrap:wrap;justify-content:center;margin-bottom:2rem;position:relative;z-index:1}
.pcard{background:rgba(255,255,255,.07);border:1.5px solid rgba(255,255,255,.12);border-radius:20px;padding:1.75rem 1.25rem;cursor:pointer;text-align:center;width:132px;transition:all .2s;backdrop-filter:blur(12px)}
.pcard:hover{transform:translateY(-4px);background:rgba(255,255,255,.14);border-color:rgba(255,255,255,.3)}
.pcard.sel{border-color:var(--blue);background:rgba(37,99,235,.22)}
.pcard-av{font-size:2.25rem;margin-bottom:.6rem}
.pcard-name{color:#fff;font-weight:800;font-size:1rem}
.pcard-role{color:rgba(255,255,255,.45);font-size:.75rem;margin-top:.2rem}
.auth-box{background:rgba(255,255,255,.08);border:1.5px solid rgba(255,255,255,.15);border-radius:20px;padding:1.75rem;width:100%;max-width:320px;backdrop-filter:blur(12px);animation:slideUp .3s ease;position:relative;z-index:1}
@keyframes slideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
.auth-label{color:rgba(255,255,255,.65);font-size:.8rem;font-weight:700;letter-spacing:.06em;text-transform:uppercase;margin-bottom:.5rem}
.auth-input{width:100%;padding:.85rem 1rem;border-radius:12px;border:1.5px solid rgba(255,255,255,.2);background:rgba(255,255,255,.1);color:#fff;font-size:1.05rem;font-family:'Nunito',sans-serif;outline:none;transition:border .2s}
.auth-input:focus{border-color:var(--blue)}
.auth-input::placeholder{color:rgba(255,255,255,.3)}
.pin-row{display:flex;gap:.5rem;justify-content:center;margin:.75rem 0}
.pin-dot{width:48px;height:48px;border-radius:12px;border:1.5px solid rgba(255,255,255,.2);background:rgba(255,255,255,.1);display:flex;align-items:center;justify-content:center;font-size:1.4rem;color:#fff;font-weight:800}
.pin-dot.filled{background:var(--blue);border-color:var(--blue)}
.numpad{display:grid;grid-template-columns:repeat(3,1fr);gap:.6rem;margin-top:.75rem}
.nkey{padding:.9rem;border-radius:11px;background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.1);color:#fff;font-size:1.2rem;font-weight:700;cursor:pointer;transition:all .15s;font-family:'Nunito',sans-serif}
.nkey:hover{background:rgba(255,255,255,.2)}
.nkey:active{transform:scale(.95)}
.auth-err{color:#fca5a5;font-size:.85rem;margin-top:.65rem;text-align:center}
.auth-ok{color:#86efac;font-size:.85rem;margin-top:.65rem;text-align:center}
.login-version{position:absolute;bottom:1.25rem;left:50%;transform:translateX(-50%);color:rgba(255,255,255,.3);font-size:.72rem;font-weight:600;white-space:nowrap;z-index:1}

/* TOPBAR */
.topbar{background:var(--topbar-bg);border-bottom:1px solid var(--border);padding:.9rem 1.5rem;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:100;box-shadow:0 1px 8px rgba(0,0,0,.05);transition:background .3s,border-color .3s}
.topbar-l{display:flex;align-items:center;gap:.7rem}
.tb-av{font-size:1.4rem;cursor:pointer}
.tb-name{font-weight:800;font-size:.95rem;color:var(--ink)}
.tb-sub{font-size:.72rem;color:var(--muted)}
.topbar-r{display:flex;align-items:center;gap:.6rem}
.streak-pill{display:flex;align-items:center;gap:.3rem;background:var(--amber-light);color:var(--amber);padding:.35rem .8rem;border-radius:999px;font-weight:800;font-size:.85rem}
.streak-tip{font-size:.65rem;opacity:.7;font-weight:600}
.icon-btn{background:none;border:1.5px solid var(--border);border-radius:10px;padding:.35rem .65rem;cursor:pointer;font-size:1rem;transition:all .15s;color:var(--ink)}
.icon-btn:hover{border-color:var(--blue)}
.logout-btn{background:none;border:1.5px solid var(--border);border-radius:10px;padding:.35rem .8rem;color:var(--muted);font-size:.82rem;cursor:pointer;font-family:'Nunito',sans-serif;font-weight:700;transition:all .15s}
.logout-btn:hover{border-color:var(--red);color:var(--red)}
.version-chip{font-size:.68rem;font-weight:700;color:var(--muted);background:var(--surface2);padding:.2rem .6rem;border-radius:999px}

/* MAIN */
.main{flex:1;padding:1.5rem;max-width:880px;margin:0 auto;width:100%}
.card{background:var(--surface);border-radius:var(--radius);border:1px solid var(--border);box-shadow:var(--shadow);padding:1.5rem;margin-bottom:1.25rem;transition:background .3s,border-color .3s}
.card-title{font-weight:800;font-size:1.05rem;color:var(--navy);margin-bottom:.25rem}
.card-sub{font-size:.82rem;color:var(--muted)}

/* HERO */
.hero-card{background:var(--hero-grad);border-radius:24px;padding:2rem;color:#fff;margin-bottom:1.25rem;position:relative;overflow:hidden}
.hero-card::after{content:'';position:absolute;right:-30px;top:-30px;width:180px;height:180px;border-radius:50%;background:rgba(255,255,255,.05)}
.hero-lbl{font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;opacity:.55;margin-bottom:.4rem}
.hero-title{font-family:'Lora',serif;font-size:1.55rem;font-weight:700;margin-bottom:.35rem}
.hero-meta{opacity:.65;font-size:.85rem;margin-bottom:1.4rem}
.hero-btn{display:inline-flex;align-items:center;gap:.45rem;background:#fff;color:#1e3a5f;font-weight:800;padding:.85rem 1.75rem;border-radius:13px;border:none;font-size:.95rem;cursor:pointer;font-family:'Nunito',sans-serif;transition:all .2s;box-shadow:0 4px 14px rgba(0,0,0,.18)}
.hero-btn:hover{transform:translateY(-2px);box-shadow:0 6px 22px rgba(0,0,0,.22)}
.hero-btn:disabled{opacity:.5;cursor:not-allowed;transform:none}

/* STATS */
.stats-row{display:grid;grid-template-columns:repeat(3,1fr);gap:1rem;margin-bottom:1.25rem}
.stat-card{background:var(--surface);border-radius:var(--radius);border:1px solid var(--border);padding:1.2rem;text-align:center;transition:background .3s}
.stat-num{font-size:1.9rem;font-weight:900;color:var(--navy);line-height:1}
.stat-lbl{font-size:.7rem;color:var(--muted);font-weight:600;margin-top:.3rem;text-transform:uppercase;letter-spacing:.05em}
.stat-tip{font-size:.65rem;color:var(--muted);margin-top:.2rem;font-style:italic}

/* BADGES */
.badges-grid{display:flex;flex-wrap:wrap;gap:.75rem;padding:.25rem 0}
.badge-item{display:flex;flex-direction:column;align-items:center;gap:.3rem;padding:.75rem;border-radius:14px;border:1.5px solid var(--border);background:var(--surface2);width:80px;text-align:center;transition:all .2s}
.badge-item.earned{border-color:var(--amber);background:var(--amber-light)}
.badge-item.locked{opacity:.35;filter:grayscale(1)}
.badge-icon{font-size:1.75rem}
.badge-lbl{font-size:.65rem;font-weight:700;color:var(--ink);line-height:1.2}
.badge-new{position:fixed;bottom:2rem;left:50%;transform:translateX(-50%);background:var(--amber);color:#fff;font-weight:800;font-size:1rem;padding:1rem 2rem;border-radius:999px;box-shadow:0 8px 30px rgba(217,119,6,.4);z-index:999;animation:popIn .4s ease;display:flex;align-items:center;gap:.5rem}
@keyframes popIn{from{opacity:0;transform:translateX(-50%) scale(.85)}to{opacity:1;transform:translateX(-50%) scale(1)}}

/* SESSION ROWS */
.session-row{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:.5rem;padding:.7rem 0;border-bottom:1px solid var(--border);font-size:.85rem;align-items:center}
.session-row:last-child{border-bottom:none}
.session-row.hdr{font-weight:700;color:var(--muted);font-size:.72rem;text-transform:uppercase;letter-spacing:.05em}
.score-pill{display:inline-block;padding:.18rem .55rem;border-radius:999px;font-weight:700;font-size:.78rem}
.score-pill.g{background:var(--green-light);color:var(--green)}
.score-pill.m{background:var(--amber-light);color:var(--amber)}
.score-pill.l{background:var(--red-light);color:var(--red)}

/* MINI BARS */
.mini-bars{display:flex;align-items:flex-end;gap:3px;height:44px}
.mini-bar{flex:1;border-radius:3px 3px 0 0;min-width:10px}

/* TEST */
.test-screen{min-height:100vh;background:var(--bg);display:flex;flex-direction:column;transition:background .3s}
.test-prog-bar{height:6px;background:var(--border)}
.test-prog-fill{height:100%;background:var(--blue);transition:width .4s ease;border-radius:0 3px 3px 0}
.test-body{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2rem;gap:1.5rem}
.test-ctr{font-size:.82rem;font-weight:700;color:var(--muted);letter-spacing:.05em}
.word-card{background:var(--surface);border-radius:24px;border:1px solid var(--border);box-shadow:var(--shadow-lg);padding:2.5rem;text-align:center;width:100%;max-width:500px;transition:background .3s}
.speak-btn{width:76px;height:76px;border-radius:50%;background:var(--hero-grad);border:none;cursor:pointer;font-size:1.9rem;display:flex;align-items:center;justify-content:center;margin:0 auto 1.1rem;transition:all .2s;box-shadow:0 4px 16px rgba(0,0,0,.2)}
.speak-btn:hover{transform:scale(1.06)}
.speak-btn:active{transform:scale(.97)}
.speak-hint{font-size:.78rem;color:var(--muted);margin-bottom:1.4rem}
.ans-input{width:100%;padding:.95rem 1.2rem;font-size:1.35rem;font-weight:700;border:2.5px solid var(--border);border-radius:14px;text-align:center;font-family:'Nunito',sans-serif;outline:none;transition:border .2s,background .2s;color:var(--ink);background:var(--surface);letter-spacing:.04em}
.ans-input:focus{border-color:var(--blue)}
.ans-input.ok{border-color:var(--green);background:var(--green-light);color:var(--green)}
.ans-input.bad{border-color:var(--red);background:var(--red-light);color:var(--red)}
.submit-btn{width:100%;padding:.95rem;background:var(--blue);color:#fff;border:none;border-radius:13px;font-size:1.05rem;font-weight:800;cursor:pointer;font-family:'Nunito',sans-serif;transition:all .2s;margin-top:.9rem}
.submit-btn:hover{filter:brightness(1.08)}
.submit-btn:disabled{opacity:.4;cursor:not-allowed}
.fb-banner{width:100%;max-width:500px;border-radius:13px;padding:.9rem 1.1rem;font-weight:700;font-size:.92rem;text-align:center;display:flex;align-items:center;justify-content:center;gap:.45rem}
.fb-banner.ok{background:var(--green-light);color:var(--green)}
.fb-banner.bad{background:var(--red-light);color:var(--red)}
.next-btn{padding:.85rem 2.25rem;background:var(--navy);color:#fff;border:none;border-radius:13px;font-size:.95rem;font-weight:800;cursor:pointer;font-family:'Nunito',sans-serif;transition:all .2s}
.next-btn:hover{opacity:.88}
@keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-6px)}60%{transform:translateX(6px)}80%{transform:translateX(-3px)}}
.shake{animation:shake .4s ease}

/* RESULTS */
.results-wrap{min-height:100vh;background:var(--bg);padding:2rem;display:flex;flex-direction:column;align-items:center;transition:background .3s}
.score-ring{width:136px;height:136px;border-radius:50%;display:flex;flex-direction:column;align-items:center;justify-content:center;border:8px solid;margin:1.4rem auto}
.score-big{font-size:2.4rem;font-weight:900;line-height:1}
.score-frac{font-size:.82rem;font-weight:700;opacity:.65}
.missed-list{list-style:none;width:100%;max-width:500px}
.missed-item{display:flex;align-items:center;justify-content:space-between;padding:.7rem 1rem;border-radius:10px;margin-bottom:.45rem;background:var(--surface);border:1px solid var(--border)}
.m-wrong{text-decoration:line-through;color:var(--red);font-weight:600}
.m-right{color:var(--green);font-weight:700}
.done-btn{margin-top:1.4rem;padding:.95rem 2.75rem;background:var(--blue);color:#fff;border:none;border-radius:13px;font-size:1.05rem;font-weight:800;cursor:pointer;font-family:'Nunito',sans-serif;transition:all .2s}
.done-btn:hover{filter:brightness(1.08)}

/* TABS */
.tab-row{display:flex;gap:.45rem;margin-bottom:1.4rem;flex-wrap:wrap}
.tab-btn{padding:.55rem 1.15rem;border-radius:10px;border:1.5px solid var(--border);background:var(--surface);font-weight:700;font-size:.85rem;cursor:pointer;font-family:'Nunito',sans-serif;color:var(--muted);transition:all .15s}
.tab-btn.on{background:var(--navy);color:#fff;border-color:var(--navy)}

/* PARENT STATS */
.cs-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:1rem;margin-bottom:1rem}
.cs-card{background:var(--surface);border:1px solid var(--border);border-radius:14px;padding:1rem;text-align:center;transition:background .3s}
.cs-num{font-size:1.55rem;font-weight:900;color:var(--navy)}
.cs-lbl{font-size:.68rem;color:var(--muted);font-weight:600;text-transform:uppercase;letter-spacing:.05em;margin-top:.2rem}

/* WORD MANAGER */
.wm-input,.wm-select{flex:1;padding:.7rem .95rem;border:1.5px solid var(--border);border-radius:11px;font-size:.92rem;font-family:'Nunito',sans-serif;outline:none;transition:border .2s;color:var(--ink);background:var(--surface)}
.wm-input:focus,.wm-select:focus{border-color:var(--blue)}
.wm-textarea{width:100%;padding:.7rem .95rem;border:1.5px solid var(--border);border-radius:11px;font-size:.92rem;font-family:'Nunito',sans-serif;outline:none;min-height:110px;resize:vertical;color:var(--ink);background:var(--surface);transition:border .2s}
.wm-textarea:focus{border-color:var(--blue)}
.wm-btn{padding:.7rem 1.4rem;background:var(--blue);color:#fff;border:none;border-radius:11px;font-weight:800;font-size:.9rem;cursor:pointer;font-family:'Nunito',sans-serif;transition:all .15s}
.wm-btn:hover{filter:brightness(1.08)}
.wm-btn.danger{background:var(--red)}
.wm-btn.secondary{background:var(--surface2);color:var(--ink);border:1.5px solid var(--border)}
.wm-btn.secondary:hover{border-color:var(--blue);color:var(--blue)}
.list-item{display:flex;align-items:center;justify-content:space-between;padding:.95rem 1.15rem;background:var(--surface);border-radius:13px;border:1.5px solid var(--border);cursor:pointer;transition:all .15s;margin-bottom:.45rem}
.list-item:hover{border-color:var(--blue)}
.list-item.sel{border-color:var(--blue);background:var(--blue-light)}
.list-name{font-weight:700;color:var(--ink)}
.list-meta{font-size:.77rem;color:var(--muted);margin-top:.2rem}
.word-chip{display:inline-flex;align-items:center;gap:.35rem;background:var(--blue-light);color:var(--blue);padding:.3rem .7rem;border-radius:999px;font-weight:700;font-size:.82rem;margin:.2rem}
.chip-del{cursor:pointer;background:none;border:none;color:inherit;font-size:.95rem;opacity:.55;font-family:'Nunito',sans-serif}
.chip-del:hover{opacity:1}
.words-cloud{display:flex;flex-wrap:wrap;gap:.15rem;padding:.4rem 0}
.empty-state{text-align:center;padding:2.5rem 1rem;color:var(--muted)}

/* ICON PICKER */
.icon-picker{background:var(--surface);border:1.5px solid var(--border);border-radius:16px;padding:1.25rem;margin-top:.5rem}
.icon-cat-tabs{display:flex;gap:.35rem;flex-wrap:wrap;margin-bottom:1rem}
.icon-cat-btn{padding:.3rem .7rem;border-radius:8px;border:1.5px solid var(--border);background:var(--surface2);font-size:.78rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;color:var(--muted);transition:all .15em}
.icon-cat-btn.on{background:var(--blue);color:#fff;border-color:var(--blue)}
.icon-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:.5rem}
.icon-opt{font-size:1.6rem;cursor:pointer;padding:.4rem;border-radius:10px;border:2px solid transparent;text-align:center;transition:all .15s}
.icon-opt:hover{background:var(--surface2)}
.icon-opt.chosen{border-color:var(--blue);background:var(--blue-light)}

/* THEME PICKER */
.theme-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:.75rem;margin-top:.5rem}
.theme-opt{border-radius:14px;border:2.5px solid var(--border);cursor:pointer;overflow:hidden;transition:all .2s}
.theme-opt.chosen{border-color:var(--blue)}
.theme-swatch{height:48px}
.theme-label{padding:.4rem;text-align:center;font-size:.78rem;font-weight:700;color:var(--ink);background:var(--surface)}

/* REWARDS */
.points-banner{background:var(--hero-grad);border-radius:16px;padding:1.1rem 1.4rem;display:flex;align-items:center;justify-content:space-between;margin-bottom:1.25rem;color:#fff}
.points-big{font-size:2rem;font-weight:900;line-height:1}
.points-lbl{font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.07em;opacity:.65;margin-top:.15rem}
.points-earned{font-size:.85rem;font-weight:700;background:rgba(255,255,255,.18);padding:.35rem .8rem;border-radius:999px}
.reward-card{background:var(--surface);border:1.5px solid var(--border);border-radius:16px;padding:1.1rem 1.25rem;margin-bottom:.65rem;display:flex;align-items:center;justify-content:space-between;transition:all .2s}
.reward-card:hover{border-color:var(--blue)}
.reward-emoji{font-size:1.75rem;margin-right:.85rem}
.reward-name{font-weight:700;color:var(--ink);font-size:.95rem}
.reward-cost{font-size:.82rem;font-weight:700;color:var(--amber);margin-top:.15rem}
.reward-btn{padding:.55rem 1.1rem;border-radius:10px;border:none;font-weight:800;font-size:.82rem;cursor:pointer;font-family:'Nunito',sans-serif;transition:all .15s}
.reward-btn.can{background:var(--blue);color:#fff}
.reward-btn.can:hover{filter:brightness(1.08)}
.reward-btn.cant{background:var(--surface2);color:var(--muted);cursor:not-allowed}
.reward-btn.pending{background:var(--amber-light);color:var(--amber);cursor:not-allowed}
.req-card{background:var(--surface);border:1.5px solid var(--border);border-radius:14px;padding:1rem 1.25rem;margin-bottom:.6rem;transition:background .3s}
.req-card.pending{border-color:var(--amber)}
.req-card.approved{border-color:var(--green);background:var(--green-light)}
.req-card.declined{border-color:var(--red);opacity:.6}
.req-status{display:inline-block;font-size:.72rem;font-weight:700;padding:.18rem .55rem;border-radius:999px;margin-left:.5rem}
.req-status.pending{background:var(--amber-light);color:var(--amber)}
.req-status.approved{background:var(--green-light);color:var(--green)}
.req-status.declined{background:var(--red-light);color:var(--red)}
.custom-req-form{background:var(--surface2);border-radius:14px;padding:1.1rem;margin-top:1rem}
.pts-breakdown{display:flex;flex-wrap:wrap;gap:.45rem;margin-top:.6rem}
.pts-pill{background:var(--amber-light);color:var(--amber);font-size:.78rem;font-weight:700;padding:.2rem .65rem;border-radius:999px}
/* MEANING QUIZ */
.mq-screen{min-height:100vh;background:var(--bg);display:flex;flex-direction:column;transition:background .3s}
.mq-body{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2rem;gap:1.25rem;max-width:600px;margin:0 auto;width:100%}
.mq-word{font-family:'Lora',serif;font-size:2rem;font-weight:700;color:var(--navy);text-align:center;padding:1.5rem;background:var(--surface);border-radius:20px;border:1px solid var(--border);width:100%;box-shadow:var(--shadow)}
.mq-prompt{font-size:.82rem;color:var(--muted);font-weight:600;text-align:center;text-transform:uppercase;letter-spacing:.06em}
.mq-choices{display:flex;flex-direction:column;gap:.75rem;width:100%}
.mq-choice{padding:1rem 1.25rem;background:var(--surface);border:2px solid var(--border);border-radius:14px;font-size:.92rem;font-weight:600;cursor:pointer;transition:all .2s;text-align:left;font-family:'Nunito',sans-serif;color:var(--ink);line-height:1.4}
.mq-choice:hover:not(:disabled){border-color:var(--blue);background:var(--blue-light)}
.mq-choice.correct{border-color:var(--green);background:var(--green-light);color:var(--green)}
.mq-choice.wrong{border-color:var(--red);background:var(--red-light);color:var(--red)}
.mq-choice.reveal{border-color:var(--green);background:var(--green-light);color:var(--green)}
.mq-choice:disabled{cursor:not-allowed}
.mq-loading{text-align:center;color:var(--muted);font-size:.92rem;padding:2rem}
/* REWARD EDIT */
.reward-edit-form{background:var(--surface2);border-radius:12px;padding:.85rem;margin-top:.65rem;display:flex;flex-direction:column;gap:.5rem}
.profile-section{padding:1.15rem;background:var(--surface2);border-radius:14px;margin-bottom:1rem}
.profile-section-title{font-weight:800;font-size:.9rem;color:var(--navy);margin-bottom:.75rem}
.inline-row{display:flex;gap:.65rem;align-items:center;flex-wrap:wrap}
.save-indicator{font-size:.78rem;color:var(--green);font-weight:700;animation:fadeIn .3s ease}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}

/* FILE UPLOAD */
.file-drop{border:2px dashed var(--border);border-radius:12px;padding:1.5rem;text-align:center;cursor:pointer;transition:all .2s;color:var(--muted);font-weight:600;font-size:.88rem}
.file-drop:hover,.file-drop.over{border-color:var(--blue);color:var(--blue);background:var(--blue-light)}
.upload-tabs{display:flex;gap:.5rem;margin-bottom:.75rem}
.upload-tab{padding:.4rem .9rem;border-radius:8px;border:1.5px solid var(--border);background:var(--surface2);font-size:.8rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;color:var(--muted);transition:all .15s}
.upload-tab.on{background:var(--blue);color:#fff;border-color:var(--blue)}

/* DICTIONARY POPUP */
.dict-overlay{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:200;display:flex;align-items:center;justify-content:center;padding:1.5rem;animation:fadeIn .2s ease}
.dict-card{background:var(--surface);border-radius:20px;padding:1.75rem;width:100%;max-width:480px;box-shadow:var(--shadow-lg);animation:slideUp .25s ease;max-height:80vh;overflow-y:auto}
.dict-word{font-family:'Lora',serif;font-size:1.6rem;font-weight:700;color:var(--navy)}
.dict-phonetic{font-size:.88rem;color:var(--muted);margin-bottom:1rem}
.dict-meaning{margin-bottom:1rem;padding-bottom:1rem;border-bottom:1px solid var(--border)}
.dict-meaning:last-child{border-bottom:none;margin-bottom:0;padding-bottom:0}
.dict-pos{display:inline-block;font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--blue);background:var(--blue-light);padding:.15rem .55rem;border-radius:999px;margin-bottom:.4rem}
.dict-def{font-size:.92rem;color:var(--ink);line-height:1.5}
.dict-ex{font-size:.82rem;color:var(--muted);font-style:italic;margin-top:.3rem}
.dict-close{width:100%;margin-top:1.25rem;padding:.8rem;background:var(--surface2);border:1.5px solid var(--border);border-radius:12px;font-weight:700;font-size:.9rem;cursor:pointer;font-family:'Nunito',sans-serif;color:var(--ink);transition:all .15s}
.dict-close:hover{border-color:var(--blue);color:var(--blue)}
.dict-btn{background:none;border:none;cursor:pointer;font-size:1rem;padding:.2rem .4rem;border-radius:8px;transition:all .15s;color:var(--muted)}
.dict-btn:hover{background:var(--blue-light);color:var(--blue)}
.preview-list{display:flex;flex-wrap:wrap;gap:.35rem;margin:.75rem 0}
.preview-chip{display:inline-flex;align-items:center;gap:.3rem;background:var(--surface2);border:1px solid var(--border);padding:.25rem .65rem;border-radius:999px;font-size:.82rem;font-weight:600;color:var(--ink)}
.preview-chip .chip-del{font-size:.9rem}

@media(max-width:600px){
  .stats-row{gap:.5rem}
  .session-row{grid-template-columns:1fr 1fr 1fr;font-size:.78rem}
  .session-row>*:last-child{display:none}
  .main{padding:1rem}
  .hero-card{padding:1.5rem}
  .theme-grid{grid-template-columns:repeat(2,1fr)}
  .icon-grid{grid-template-columns:repeat(5,1fr)}
}
`;

export default function App() {
  const [data, setData] = useState(() => loadLocal());
  const [settings, setSettings] = useState(() => getInitialSettings());
  const [user, setUser] = useState(null);
  const [screen, setScreen] = useState("home");
  const [testWords, setTestWords] = useState([]);
  const [lastRes, setLastRes] = useState(null);
  const [lastTime, setLastTime] = useState(0);
  const [lastPts, setLastPts] = useState(0);
  const [newBadges, setNewBadges] = useState([]);
  const [meaningWords, setMeaningWords] = useState([]);
  const [lastMeaningRes, setLastMeaningRes] = useState(null);
  const [lastMeaningTime, setLastMeaningTime] = useState(0);
  const [lastMeaningPts, setLastMeaningPts] = useState(0);
  const [synonymWords, setSynonymWords] = useState([]);
  const [lastSynonymRes, setLastSynonymRes] = useState(null);
  const [lastSynonymTime, setLastSynonymTime] = useState(0);
  const [lastSynonymPts, setLastSynonymPts] = useState(0);
  const [antonymWords, setAntonymWords] = useState([]);
  const [lastAntonymRes, setLastAntonymRes] = useState(null);
  const [lastAntonymTime, setLastAntonymTime] = useState(0);
  const [lastAntonymPts, setLastAntonymPts] = useState(0);
  const [showTopbarProfile, setShowTopbarProfile] = useState(false);
  const [pendingResume, setPendingResume] = useState(null);
  const [testResumeState, setTestResumeState] = useState(null);
  const [meaningResumeState, setMeaningResumeState] = useState(null);
  const [synonymResumeState, setSynonymResumeState] = useState(null);
  const [antonymResumeState, setAntonymResumeState] = useState(null);

  // Register service worker for PWA / offline support
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/service-worker.js").catch(() => {});
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply the current user's theme on login / data change
  const currentUserData = user ? data.users.find(u => u.id === user.id) : null;
  useEffect(() => {
    applyTheme(currentUserData?.theme || "ocean");
  }, [currentUserData?.theme]);

  // Set browser tab title from settings
  useEffect(() => {
    document.title = settings.appName || "SpellWise Kids";
  }, [settings.appName]);

  // Sync from Firebase on mount — data and settings separately
  useEffect(() => {
    if (DEMO_MODE) return;
    fbGet("data").then(remote => {
      if (remote) { const d = { ...SEED, ...remote }; setData(d); saveLocal(d); }
    });
    fbGet("settings").then(remote => {
      if (remote) {
        const s = mergeSettings(APP_CONFIG, remote);
        setSettings(s);
        saveSettingsLocal(s);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync from Firebase whenever app comes back into focus (e.g. after tapping email link)
  useEffect(() => {
    if (DEMO_MODE) return;
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        fbGet("data").then(remote => {
          if (remote) { const d = { ...SEED, ...remote }; setData(d); saveLocal(d); }
        });
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle reward action URL params (parent taps Approve/Decline link from email)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get("rewardAction");
    const reqId  = params.get("reqId");
    const status = params.get("status"); // "approved" | "declined"
    if (action === "done" && reqId && status) {
      // Netlify function already updated Firebase — just sync fresh data and show confirmation
      if (!DEMO_MODE) {
        fbGet("data").then(remote => {
          if (remote) { const d = { ...SEED, ...remote }; setData(d); saveLocal(d); }
        });
      }
      // Clean URL without reload
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function persist(newData) { setData(newData); saveLocal(newData); if (!DEMO_MODE) fbSet("data", newData); }
  function persistSettings(newSettings) { setSettings(newSettings); saveSettingsLocal(newSettings); if (!DEMO_MODE) fbSet("settings", newSettings); }
  function handleLogin(u) {
    setUser(u);
    setScreen(u.role === "child" && u.mustResetPin ? "forceResetPin" : "home");
  }
  function handlePinReset(newPin) {
    const updatedUsers = data.users.map(u => u.id === user.id ? { ...u, pin: newPin, mustResetPin: false } : u);
    const newData = { ...data, users: updatedUsers };
    persist(newData);
    setUser(prev => ({ ...prev, pin: newPin, mustResetPin: false }));
    setScreen("home");
  }
  function handleLogout() { setUser(null); setScreen("home"); applyTheme("ocean"); }

  function _doStartTest() {
    const pool = buildMasterPool(data.wordLists);
    if (pool.length === 0) return;
    setTestResumeState(null);
    setTestWords(selectWords(data.wordLists, data.progress, user.id, Math.min(settings.spellingWordCount || 20, pool.length)));
    setScreen("test");
  }
  function startTest() {
    const draft = data.quizDrafts?.[user.id]?.spelling;
    if (draft && draft.expiresAt > Date.now()) { setPendingResume({ quizType: "spelling", draft }); setScreen("resumePrompt"); return; }
    _doStartTest();
  }

  function _doStartMeaning() {
    const pool = buildMasterPool(data.wordLists);
    if (pool.length === 0) return;
    setMeaningResumeState(null);
    setMeaningWords(selectMeaningWords(data.wordLists, data.meaningProgress || {}, user.id, Math.min(settings.meaningWordCount || 10, pool.length)));
    setScreen("meaning");
  }
  function startMeaningTest() {
    const draft = data.quizDrafts?.[user.id]?.meaning;
    if (draft && draft.expiresAt > Date.now()) { setPendingResume({ quizType: "meaning", draft }); setScreen("resumePrompt"); return; }
    _doStartMeaning();
  }

  function _doStartSynonym() {
    const pool = buildMasterPool(data.wordLists);
    if (pool.length === 0) return;
    setSynonymResumeState(null);
    const count = Math.min(settings.synonymWordCount || 10, pool.length);
    setSynonymWords([...pool].sort(() => Math.random() - 0.5).slice(0, count));
    setScreen("synonyms");
  }
  function startSynonymTest() {
    const draft = data.quizDrafts?.[user.id]?.synonym;
    if (draft && draft.expiresAt > Date.now()) { setPendingResume({ quizType: "synonym", draft }); setScreen("resumePrompt"); return; }
    _doStartSynonym();
  }

  function _doStartAntonym() {
    const pool = buildMasterPool(data.wordLists);
    if (pool.length === 0) return;
    setAntonymResumeState(null);
    const count = Math.min(settings.antonymWordCount || 10, pool.length);
    setAntonymWords([...pool].sort(() => Math.random() - 0.5).slice(0, count));
    setScreen("antonyms");
  }
  function startAntonymTest() {
    const draft = data.quizDrafts?.[user.id]?.antonym;
    if (draft && draft.expiresAt > Date.now()) { setPendingResume({ quizType: "antonym", draft }); setScreen("resumePrompt"); return; }
    _doStartAntonym();
  }

  function handleSaveAndExit(quizType, draftData) {
    const newData = { ...data, quizDrafts: { ...(data.quizDrafts || {}), [user.id]: { ...(data.quizDrafts?.[user.id] || {}), [quizType]: draftData } } };
    persist(newData);
    setScreen("home");
  }
  function handleExitQuiz() { setScreen("home"); }
  function clearDraft(quizType) {
    const newData = { ...data, quizDrafts: { ...(data.quizDrafts || {}), [user.id]: { ...(data.quizDrafts?.[user.id] || {}), [quizType]: null } } };
    persist(newData);
  }

  function finishMeaningTest(results, timeSec) {
    const answered = results.filter(r => !r.skipped);
    const correct = answered.filter(r => r.correct).length;
    const score = answered.length ? Math.round((correct / answered.length) * 100) : 0;
    let newMProg = { ...(data.meaningProgress || {}) };
    results.forEach(r => {
      if (!r.skipped) newMProg = updateMeaningProgress(newMProg, user.id, r.word.id, r.correct);
    });
    const ptsEarned = calcMeaningPoints(score);
    const newPoints = { ...(data.points || {}), [user.id]: (data.points?.[user.id] || 0) + ptsEarned };
    const now = Date.now();
    const session = { id: `ms_${now}`, userId: user.id, date: now, score, total: answered.length, type: "meaning", timeSec, ptsEarned };
    const newData = { ...data, meaningProgress: newMProg, sessions: [...data.sessions, session], points: newPoints };
    persist(newData);
    setLastMeaningRes(results); setLastMeaningTime(timeSec); setLastMeaningPts(ptsEarned);
    setScreen("meaningResults");
  }

  function finishSynonymTest(results, timeSec) {
    const answered = results.filter(r => !r.skipped);
    const correct = answered.filter(r => r.correct).length;
    const score = answered.length ? Math.round((correct / answered.length) * 100) : 0;
    let newSynProg = { ...(data.synProgress || {}) };
    results.forEach(r => {
      if (!r.skipped) newSynProg = updateSynProgress(newSynProg, user.id, r.word.id, r.correct);
    });
    const ptsEarned = calcSynonymPoints(score);
    const newPoints = { ...(data.points || {}), [user.id]: (data.points?.[user.id] || 0) + ptsEarned };
    const now = Date.now();
    const session = { id: `syn_${now}`, userId: user.id, date: now, score, total: answered.length, type: "synonym", timeSec, ptsEarned };
    const prevSessions = data.sessions.filter(s => s.userId === user.id);
    const newData = { ...data, synProgress: newSynProg, sessions: [...data.sessions, session], points: newPoints };
    const earned = checkNewBadges(user.id, { ...newData }, session, prevSessions);
    if (earned.length) { newData.badges = { ...newData.badges, [user.id]: [...(newData.badges?.[user.id] || []), ...earned] }; setNewBadges(earned.map(b => b.id)); }
    persist(newData);
    setLastSynonymRes(results); setLastSynonymTime(timeSec); setLastSynonymPts(ptsEarned);
    setScreen("synonymResults");
  }

  function finishAntonymTest(results, timeSec) {
    const answered = results.filter(r => !r.skipped);
    const correct = answered.filter(r => r.correct).length;
    const score = answered.length ? Math.round((correct / answered.length) * 100) : 0;
    let newAntProg = { ...(data.antProgress || {}) };
    results.forEach(r => {
      if (!r.skipped) newAntProg = updateAntProgress(newAntProg, user.id, r.word.id, r.correct);
    });
    const ptsEarned = calcAntonymPoints(score);
    const newPoints = { ...(data.points || {}), [user.id]: (data.points?.[user.id] || 0) + ptsEarned };
    const now = Date.now();
    const session = { id: `ant_${now}`, userId: user.id, date: now, score, total: answered.length, type: "antonym", timeSec, ptsEarned };
    const prevSessions = data.sessions.filter(s => s.userId === user.id);
    const newData = { ...data, antProgress: newAntProg, sessions: [...data.sessions, session], points: newPoints };
    const earned = checkNewBadges(user.id, { ...newData }, session, prevSessions);
    if (earned.length) { newData.badges = { ...newData.badges, [user.id]: [...(newData.badges?.[user.id] || []), ...earned] }; setNewBadges(earned.map(b => b.id)); }
    persist(newData);
    setLastAntonymRes(results); setLastAntonymTime(timeSec); setLastAntonymPts(ptsEarned);
    setScreen("antonymResults");
  }

  // Streak helper — based on lastActive timestamp, not session history
  function calcStreak(lastActive, currentStreak) {
    if (!lastActive) return 1; // very first test ever
    const now = new Date();
    const last = new Date(lastActive);
    const todayStr = now.toDateString();
    const yesterdayStr = new Date(now - 86400000).toDateString();
    const lastStr = last.toDateString();
    if (lastStr === todayStr)      return currentStreak;       // already practised today — hold
    if (lastStr === yesterdayStr)  return currentStreak + 1;   // practised yesterday — increment
    return 1;                                                   // missed 1+ days — reset
  }

  function finishTest(results, timeSec) {
    const correct = results.filter(r => r.correct).length;
    const score = Math.round((correct / results.length) * 100);
    const missed = results.filter(r => !r.correct).map(r => r.word.word);
    let newProg = { ...data.progress };
    results.forEach(r => { newProg = updateProgress(newProg, user.id, r.word.id, r.correct); });
    const prevSessions = data.sessions.filter(s => s.userId === user.id);

    // Correct streak using lastActive timestamp on the user record
    const userData = data.users.find(u => u.id === user.id);
    const curStreak = data.streaks?.[user.id] || 0;
    const newStreak = calcStreak(userData?.lastActive || null, curStreak);

    // Count newly mastered words this session
    const prevMastered = Object.entries(data.progress || {}).filter(([k, v]) => k.startsWith(user.id) && v.mastered).length;
    const newMastered = Object.entries(newProg || {}).filter(([k, v]) => k.startsWith(user.id) && v.mastered).length;
    const newlyMastered = Math.max(0, newMastered - prevMastered);

    // Calculate points earned this session
    const ptsEarned = calcPointsEarned(score, newStreak, curStreak, newlyMastered);
    const newPoints = { ...(data.points || {}), [user.id]: (data.points?.[user.id] || 0) + ptsEarned };

    // Stamp lastActive = now on the user record
    const now = Date.now();
    const updatedUsers = data.users.map(u => u.id === user.id ? { ...u, lastActive: now } : u);

    const session = { id: `s_${now}`, userId: user.id, date: now, score, total: results.length, missed, timeSec, ptsEarned };
    const newData = {
      ...data,
      users: updatedUsers,
      progress: newProg,
      sessions: [...data.sessions, session],
      streaks: { ...data.streaks, [user.id]: newStreak },
      points: newPoints,
    };
    const earned = checkNewBadges(user.id, { ...newData }, session, prevSessions);
    if (earned.length) { newData.badges = { ...newData.badges, [user.id]: [...(newData.badges?.[user.id] || []), ...earned] }; setNewBadges(earned.map(b => b.id)); }
    persist(newData);
    setLastRes(results); setLastTime(timeSec); setLastPts(ptsEarned); setScreen("results");
  }

  const bgImage = currentUserData?.bgImage || null;

  if (!user) return <PlannerLoginScreen users={data.users} onLogin={handleLogin} appName={settings.appName || "SpellWise Kids"} />;

  if (screen === "forceResetPin") return (
    <PlannerForceResetPin user={user} onComplete={handlePinReset} />
  );

  if (screen === "resumePrompt" && pendingResume) {
    const { quizType, draft } = pendingResume;
    const labels = { spelling: "Spelling", meaning: "Meaning", synonym: "Synonyms", antonym: "Antonyms" };
    return (
      <BgWrap bgImage={bgImage}>
        <style>{CSS}</style>
        <div style={{ maxWidth: 420, margin: "3rem auto", padding: "0 1.25rem" }}>
          <div className="card" style={{ padding: "1.75rem" }}>
            <div className="card-title" style={{ marginBottom: ".75rem" }}>📖 Resume {labels[quizType]} Quiz?</div>
            <div className="card-sub" style={{ marginBottom: "1.5rem" }}>
              You have an unfinished quiz from today — word {draft.currentIndex + 1} of {draft.words.length}. Continue where you left off?
            </div>
            <div style={{ display: "flex", gap: ".75rem" }}>
              <button className="wm-btn" onClick={() => {
                const rs = { currentIndex: draft.currentIndex, results: draft.results, startTime: draft.startTime };
                clearDraft(quizType);
                if (quizType === "spelling") { setTestWords(draft.words);    setTestResumeState(rs);    setScreen("test");     }
                if (quizType === "meaning")  { setMeaningWords(draft.words); setMeaningResumeState(rs); setScreen("meaning");  }
                if (quizType === "synonym")  { setSynonymWords(draft.words); setSynonymResumeState(rs); setScreen("synonyms"); }
                if (quizType === "antonym")  { setAntonymWords(draft.words); setAntonymResumeState(rs); setScreen("antonyms"); }
                setPendingResume(null);
              }}>Continue →</button>
              <button className="wm-btn secondary" onClick={() => {
                clearDraft(quizType);
                setPendingResume(null);
                if (quizType === "spelling") _doStartTest();
                if (quizType === "meaning")  _doStartMeaning();
                if (quizType === "synonym")  _doStartSynonym();
                if (quizType === "antonym")  _doStartAntonym();
              }}>Start Fresh</button>
            </div>
          </div>
        </div>
      </BgWrap>
    );
  }

  if (screen === "test") return (
    <BgWrap bgImage={bgImage}>
      <style>{CSS}</style>
      <SpellingTest
        words={testWords} onComplete={finishTest}
        voicePrefs={currentUserData?.voicePrefs} sentenceMode={currentUserData?.voicePrefs?.sentenceMode ?? true}
        onSaveAndExit={draft => handleSaveAndExit("spelling", draft)} onExit={handleExitQuiz}
        initialIdx={testResumeState?.currentIndex || 0}
        initialResults={testResumeState?.results || []}
        initialStartTime={testResumeState?.startTime}
      />
    </BgWrap>
  );

  if (screen === "meaning") return (
    <BgWrap bgImage={bgImage}>
      <style>{CSS}</style>
      <MeaningQuiz
        words={meaningWords} onComplete={finishMeaningTest}
        onSaveAndExit={draft => handleSaveAndExit("meaning", draft)} onExit={handleExitQuiz}
        initialIdx={meaningResumeState?.currentIndex || 0}
        initialResults={meaningResumeState?.results || []}
        initialStartTime={meaningResumeState?.startTime}
      />
    </BgWrap>
  );

  if (screen === "meaningResults") return (
    <BgWrap bgImage={bgImage}>
      <style>{CSS}</style>
      <MeaningResults results={lastMeaningRes} timeSec={lastMeaningTime} ptsEarned={lastMeaningPts} onDone={() => setScreen("home")} />
    </BgWrap>
  );

  if (screen === "synonyms") return (
    <BgWrap bgImage={bgImage}>
      <style>{CSS}</style>
      <SynonymQuiz
        words={synonymWords} onComplete={finishSynonymTest}
        onSaveAndExit={draft => handleSaveAndExit("synonym", draft)} onExit={handleExitQuiz}
        initialIdx={synonymResumeState?.currentIndex || 0}
        initialResults={synonymResumeState?.results || []}
        initialStartTime={synonymResumeState?.startTime}
      />
    </BgWrap>
  );

  if (screen === "synonymResults") return (
    <BgWrap bgImage={bgImage}>
      <style>{CSS}</style>
      {newBadges.length > 0 && <BadgeToast badges={newBadges} onDone={() => setNewBadges([])} />}
      <MeaningResults results={lastSynonymRes} timeSec={lastSynonymTime} ptsEarned={lastSynonymPts} onDone={() => setScreen("home")} />
    </BgWrap>
  );

  if (screen === "antonyms") return (
    <BgWrap bgImage={bgImage}>
      <style>{CSS}</style>
      <AntonymQuiz
        words={antonymWords} onComplete={finishAntonymTest}
        onSaveAndExit={draft => handleSaveAndExit("antonym", draft)} onExit={handleExitQuiz}
        initialIdx={antonymResumeState?.currentIndex || 0}
        initialResults={antonymResumeState?.results || []}
        initialStartTime={antonymResumeState?.startTime}
      />
    </BgWrap>
  );

  if (screen === "antonymResults") return (
    <BgWrap bgImage={bgImage}>
      <style>{CSS}</style>
      {newBadges.length > 0 && <BadgeToast badges={newBadges} onDone={() => setNewBadges([])} />}
      <MeaningResults results={lastAntonymRes} timeSec={lastAntonymTime} ptsEarned={lastAntonymPts} onDone={() => setScreen("home")} />
    </BgWrap>
  );

  if (screen === "results") return (
    <BgWrap bgImage={bgImage}>
      <style>{CSS}</style>
      {newBadges.length > 0 && <BadgeToast badges={newBadges} onDone={() => setNewBadges([])} />}
      <ResultsScreen results={lastRes} timeSec={lastTime} ptsEarned={lastPts} onDone={() => setScreen("home")} />
    </BgWrap>
  );

  /* ── Parent gets the new Planner layout (no old topbar) ── */
  if (user.role === "parent") {
    return (
      <>
        <style>{CSS}</style>
        {newBadges.length > 0 && <BadgeToast badges={newBadges} onDone={() => setNewBadges([])} />}
        <PlannerDashboard
          data={data}
          users={data.users}
          onDataChange={persist}
          settings={settings}
          onSettingsChange={persistSettings}
          onLogout={handleLogout}
        />
      </>
    );
  }

  /* ── Child keeps the existing layout ── */
  return (
    <BgWrap bgImage={bgImage}>
      <style>{CSS}</style>
      {newBadges.length > 0 && <BadgeToast badges={newBadges} onDone={() => setNewBadges([])} />}
      <div className="app">
        <div className="topbar">
          <div className="topbar-l"
            onClick={() => setShowTopbarProfile(true)}
            style={{ cursor: "pointer" }}
            title="My profile"
          >
            <span className="tb-av">{currentUserData?.avatar || user.avatar}</span>
            <div>
              <div className="tb-name">{user.name}</div>
              <div className="tb-sub">Year {user.yearLevel}</div>
            </div>
            <span style={{ fontSize: ".8rem", color: "var(--muted)", marginLeft: ".25rem", alignSelf: "center" }}>⚙️</span>
          </div>
          <div className="topbar-r">
            <div className="streak-pill" title="Number of consecutive days you've practised">
              🔥 {data.streaks?.[user.id] || 0}
              <span className="streak-tip">day streak</span>
            </div>
            <NotificationBell userId={user.id} data={data} onDataChange={persist} />
            <span className="version-chip">v{APP_VERSION}</span>
            <button className="logout-btn" onClick={handleLogout}>Sign Out</button>
          </div>
        </div>
        <div className="main">
          {showTopbarProfile
            ? <ProfileSettings user={user} data={data} onDataChange={persist} onClose={() => setShowTopbarProfile(false)} />
            : <ChildHome user={user} data={data} onStartTest={startTest} onStartMeaningTest={startMeaningTest} onStartSynonymTest={startSynonymTest} onStartAntonymTest={startAntonymTest} onDataChange={persist} settings={settings} />}
        </div>
      </div>
    </BgWrap>
  );
}
